import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { EventSeat, Group } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import moment from "moment";

const countries = [
  { code: "+60", label: "MY" }, // Malaysia
  { code: "+65", label: "SG" }, // Singapore
  { code: "+66", label: "TH" }, // Thailand
];

const BookTable = ({ prefilled = {} }) => {
  const [formData, setFormData] = useState({
    name: '',
    countryCode: '+60',
    phone: '',
    email: '',
    source: '',
    date: prefilled.date ? new Date(prefilled.date) : new Date(),
    time: prefilled.time || '',
    numberOfGuests: prefilled.numberOfGuests || '',
    specialRequests: '',
  });

  const [availableTables, setAvailableTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(prefilled.table ? prefilled.table : null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [timeSlots, setTimeSlots] = useState([]);

  const navigate = useNavigate();

  const checkAvailability = async () => {
    if (!formData.date || !formData.time || !formData.numberOfGuests) return;
    setLoading(true);
    setError('');

    try {
      const response = await api.get('/tables/available', {
        params: {
          date: formData.date.toISOString().split('T')[0],
          time: formData.time,
          numberOfGuests: formData.numberOfGuests,
        },
      });
      setAvailableTables(response.data);
    } catch (err) {
      setError('Failed to check table availability');
    } finally {
      setLoading(false);
    }
  };

  const getTimeSlot = async () => {
    try {
      const timeSlotsRes = await api.get('/settings/time-slots');
      setTimeSlots(timeSlotsRes.data.timeSlots);
    } catch (err) {
      setError('Failed to load time slots');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTimeSlot();
    checkAvailability();
    console.log(selectedTable, "selectedTable");
  }, [formData.date, formData.time, formData.numberOfGuests, selectedTable]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTable) {
      setError('Please select a table');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const bookingData = {
        tableId: selectedTable._id,
        name: formData.name,
        phone: `${formData.countryCode}${formData.phone}`, // combine code + phone
        email: formData.email,
        source: formData.source,
        date: formData.date.toISOString().split('T')[0],
        time: formData.time,
        numberOfGuests: parseInt(formData.numberOfGuests),
        specialRequests: formData.specialRequests,
      };

      await api.post('/bookings', bookingData);
      setSuccess('Table booked successfully!');
      setTimeout(() => {
        if (window.location.pathname.replace("/", "") === 'admin/bookings') {
          window.location.reload();
        } else {
          navigate('/admin/bookings');
        }
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to book table');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Book a Table
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
          {/* Booking Form */}
          <Paper elevation={3} sx={{ p: 3, flex: 1 }}>
            <Box component="form" onSubmit={handleSubmit}>
              <DatePicker
                label="Date"
                value={formData.date}
                onChange={(newDate) => setFormData({ ...formData, date: newDate })}
                renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                minDate={new Date()}
              />
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="normal"
                required
              />

              {/* Phone with country code */}
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                margin="normal"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FormControl variant="standard" sx={{ minWidth: 80 }}>
                        <Select
                          value={formData.countryCode}
                          onChange={(e) =>
                            setFormData({ ...formData, countryCode: e.target.value })
                          }
                          disableUnderline
                        >
                          {countries.map((c) => (
                            <MenuItem key={c.code} value={c.code}>
                              {c.label} {c.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Email (Optional)"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                margin="normal"
              />

              <FormControl fullWidth margin="normal" required>
                <InputLabel>Source</InputLabel>
                <Select
                  value={formData.source}
                  label="Source"
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                >
                  <MenuItem value="walk-in">Walk-in</MenuItem>
                  <MenuItem value="phone">Phone</MenuItem>
                  <MenuItem value="website">Website</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <InputLabel>Time</InputLabel>
                <Select
                  value={formData.time}
                  label="Time"
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                >
                  {timeSlots.map((time) => (
                    <MenuItem key={time} value={time}>
                      {moment(time, 'HH:mm').format('hh:mm A')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <InputLabel>Number of Guests</InputLabel>
                <Select
                  value={formData.numberOfGuests}
                  label="Number of Guests"
                  onChange={(e) => setFormData({ ...formData, numberOfGuests: e.target.value })}
                >
                  {[...Array(10)].map((_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {i + 1} {i + 1 === 1 ? 'Guest' : 'Guests'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Special Requests"
                multiline
                rows={3}
                value={formData.specialRequests}
                onChange={(e) => setFormData({ ...formData, specialRequests: e.target.value })}
                margin="normal"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !selectedTable}
                sx={{ mt: 3 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Book Table'}
              </Button>
            </Box>
          </Paper>

          {/* Available Tables */}
          <Paper
            elevation={3}
            sx={{
              p: 2,
              flex: 1.5,
              maxHeight: '80vh',
              overflowY: 'auto',
              backgroundColor: '#f9f9f9',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Available Tables
            </Typography>

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : availableTables.length > 0 ? (
              <List>
                {availableTables.map((table) => (
                  <ListItem
                    key={table._id}
                    button
                    onClick={() => setSelectedTable(table)}
                    sx={{
                      border: selectedTable?._id === table._id ? 2 : 1,
                      borderColor:
                        selectedTable?._id === table._id ? 'green' : 'grey.300',
                      borderRadius: 2,
                      backgroundColor:
                        selectedTable?._id === table._id
                          ? ' #a1d118'
                          : 'action.hover',
                      borderWidth: 5,
                      mb: 1,
                      boxShadow: 2,
                      '&:hover': {
                        boxShadow: 4,
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemIcon>
                      <EventSeat color="primary" />
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        <Typography fontWeight="bold">
                          Table {table.tableNumber}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Group fontSize="small" color="action" />
                          <Typography variant="body2">
                            {table.capacity} Guests
                          </Typography>
                          <Chip
                            label={table.location}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                    />

                    {table.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          ml: 2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {table.description}
                      </Typography>
                    )}
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                py={3}
              >
                No tables available for the selected criteria.
              </Typography>
            )}
          </Paper>
        </Box>
      </Container>
    </LocalizationProvider>
  );
};

export default BookTable;
